import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
 ActionIcon,  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Stack,
  Tabs,
  Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { ScrollArea } from "@components/ScrollArea";

import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_AFTER_UPLOAD,
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MEGABYTE,
} from "@constants/index";

import { useUpdateTreatmentPlan } from "@hooks/mutations/consultation/useUpdateTreatmentPlan";
import { useGetClinicTreatmentPlans } from "@hooks/queries/clinic/useGetClinicTreatmentPlans";

import { useAuth } from "@contexts/auth";

import { TreatmentPlan } from "@customTypes/consultation";


interface ModalProps {
  opened: boolean;
  close: () => void;
  supportStaffData: ComboboxItem[] | [];
  treatmentPlan?: Partial<TreatmentPlan> | null;
}

export const EditTreatmentPlanModal = ({
  opened,
  close,
  supportStaffData,
  treatmentPlan,
}: ModalProps) => {
  const { t } = useTranslation();
  const { consultationId, patientId } = useParams();
  const { token } = useAuth();

  const [date, setDate] = useState<Date | null>(null);
  const [treatment, setTreatment] = useState<ComboboxItem | null>(null);
  const [quantity, setQuantity] = useState<string | number>(1);
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [treatmentPlanFiles, setTreatmentPlanFiles] = useState<string[]>(
    treatmentPlan?.files as string[]
  );

  const {
    data: clinicTreatmentPlans,
    isLoading: isClinicTreatmentPlansLoading,
  } = useGetClinicTreatmentPlans();
  const {
    mutate: updateTreatmentPlan,
    isLoading: isUpdateTreatmentPlanLoading,
  } = useUpdateTreatmentPlan();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const saveTreatmentPlan = () => {
    const jsFormData = new FormData();

    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append("files", file);
      });

    jsFormData.append("consultation_id", consultationId ?? "");
    jsFormData.append("patient_id", patientId ?? "");
    jsFormData.append("quantity", quantity.toString());
    jsFormData.append("date", date ? date.toISOString() : "");
    jsFormData.append("aid", supportStaff?.value ?? "");
    jsFormData.append("service", treatment?.value ?? "");

    updateTreatmentPlan(
      { treatmentPlan: jsFormData, id: treatmentPlan?.id ?? "" },
      {
        onSuccess: () => {
          close();
          setDate(null);
          setTreatment(null);
          setQuantity(1);
          setSupportStaff(null);
          setUploadedFiles([]);
        },
      }
    );
  };

  useEffect(() => {
    if (
      treatmentPlan?.id &&
      treatmentPlan?.date &&
      treatmentPlan?.treatment &&
      treatmentPlan?.quantity &&
      treatmentPlan?.supportStaff
    ) {
      setDate(treatmentPlan.date);
      setTreatment(treatmentPlan.treatment);
      setQuantity(treatmentPlan.quantity);
      setSupportStaff(treatmentPlan.supportStaff);
      if (treatmentPlan?.files) {
        setTreatmentPlanFiles(treatmentPlan?.files as string[]);
      }
    }
  }, [treatmentPlan]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-treatment-plan")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            {treatmentPlan?.id ? (
              <Tabs.Tab value="previous-files">{t("previous-files")}</Tabs.Tab>
            ) : null}
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              <Stack>
                <Select
                  withAsterisk
                  data={clinicTreatmentPlans ?? []}
                  label={t("treatment")}
                  rightSection={
                    isClinicTreatmentPlansLoading ? (
                      <Loader size={16} color="accent" />
                    ) : null
                  }
                  placeholder={t("select-treatment")}
                  searchable
                  value={treatment?.value ?? null}
                  onChange={(_, option) => setTreatment(option)}
                />
                <NumberInput
                  label={t("quantity")}
                  min={1}
                  withAsterisk
                  defaultValue={1}
                  value={quantity}
                  onChange={setQuantity}
                />
                <Select
                  label={t("support-staff")}
                  data={supportStaffData}
                  placeholder={t("select-staff")}
                  withAsterisk
                  searchable
                  clearable
                  value={supportStaff?.value ?? null}
                  onChange={(_, option) => setSupportStaff(option)}
                />
                <DatePickerInput
                  withAsterisk
                  label={t("date")}
                  // minDate={new Date()}
                  leftSection={<IconCalendar size={16} stroke={1.5} />}
                  value={date}
                  onChange={setDate}
                />
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="previous-files" className="previous-files">
            <div className="files-wrapper">
              <div className="uploaded-files">
                <h4>{t("previously-uploaded-files")}</h4>
                <ScrollArea
                  scrollbarOrientation="vertical"
                  className={`previous-files ${
                    treatmentPlan?.files?.length &&
                    treatmentPlan.files.length > 6
                      ? "show-gradient"
                      : ""
                  }`}>
                  {treatmentPlanFiles?.length ? (
                    <ul>
                      {treatmentPlanFiles.reverse().map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {(file as string).slice(-3) === "pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={"https://" + file + `?token=${token}`}>
                              {truncateWithEllipsis(
                                (file as string).split("/")[3],
                                MAX_FILENAME_LENGTH_AFTER_UPLOAD
                              )}
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty-label">
                      {t("empty-previously-uploaded-files")}
                    </p>
                  )}
                </ScrollArea>
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} required name="dropzone" />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles?.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto" mt="sm">
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            variant="outline"
                            color="red"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt={"xl"} pb={"md"}>
          <PermissionGate permission="treatments.edit"><Button
            loading={isUpdateTreatmentPlanLoading}
            disabled={!date || !treatment || !quantity || !supportStaff}
            onClick={saveTreatmentPlan}>
            {t("submit")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
